/**
 * The onboarding journey as the desktop workspace models it.
 *
 * The steps split into two phases — before arrival and after arrival — and the
 * rail draws that split as a labelled group break. The centre column's counter
 * reads "Step 2 of 4" because it counts within the current phase, not across
 * the whole journey: the four before-arrival steps are what the tenant can
 * actually do today, and the after-arrival ones only unlock at check-in.
 */

export type StepPhase = "before" | "after";

export type StepId =
  | "welcome"
  | "identity"
  | "contract"
  | "arrival"
  | "inventory"
  | "insurance"
  | "home";

export interface OnboardingStep {
  id: StepId;
  phase: StepPhase;
  /** Shown on the rail while this is the current step ("2 minutes"). */
  minutes: number;
}

// Durations taken from the reference rail, which quotes 2 minutes for identity,
// 7 for the documents and 5 for the inventory. The steps that never show a
// duration keep a nominal 1.
export const ONBOARDING_STEPS: OnboardingStep[] = [
  { id: "welcome", phase: "before", minutes: 1 },
  { id: "identity", phase: "before", minutes: 2 },
  // { id: "contract", phase: "before", minutes: 4 },
  { id: "contract", phase: "before", minutes: 7 },
  { id: "arrival", phase: "before", minutes: 1 },
  // { id: "inventory", phase: "after", minutes: 6 },
  { id: "inventory", phase: "after", minutes: 5 },
  { id: "insurance", phase: "after", minutes: 2 },
  { id: "home", phase: "after", minutes: 1 },
];

/** Flow A ends at arrival: nothing is collected after check-in, so it has no
 *  after-arrival phase at all. Flow B is the full seven steps. */
export type Flow = "A" | "B";

export const stepsForFlow = (flow: Flow): OnboardingStep[] =>
  flow === "A"
    ? ONBOARDING_STEPS.filter((step) => step.phase === "before")
    : ONBOARDING_STEPS;

/** The after-arrival steps the back office enabled for this reservation. */
export interface StepToggles {
  inventory_step: boolean;
  insurance_step: boolean;
}

/**
 * The journey for one reservation.
 *
 * Flow A and Flow B were the only two shapes available while the step list was
 * fixed. They are really the ends of a range: the DMS now enables the inventory
 * and the insurance certificate per reservation, so a tenant may be asked for
 * one, both, or neither. Turning both off drops the after-arrival phase
 * entirely — which is Flow A — so this supersedes stepsForFlow, and that stays
 * only for screens with no reservation behind them.
 */
export const stepsForToggles = ({
  inventory_step,
  insurance_step,
}: StepToggles): OnboardingStep[] => {
  const anyAfterArrival = inventory_step || insurance_step;

  return ONBOARDING_STEPS.filter((step) => {
    if (step.id === "inventory") return inventory_step;
    if (step.id === "insurance") return insurance_step;
    // The welcome-home closer only means something if the tenant was asked for
    // something after check-in; on its own it is an empty second phase.
    if (step.id === "home") return anyAfterArrival;
    return true;
  });
};

// Superseded. The eyebrow was derived from the step's position within its
// phase, which produced "Step 1 of 3" for the inventory; the reference writes
// "Step 5 of 7 · After check-in" — a fixed label per screen with its own
// qualifier. Each panel now carries its own eyebrow copy.
//
// export interface PhasePosition {
//   /** 1-based index of the step within its own phase. */
//   index: number;
//   /** How many steps that phase holds. */
//   total: number;
// }
//
// export const positionInPhase = (
//   steps: OnboardingStep[],
//   step: OnboardingStep
// ): PhasePosition => {
//   const inPhase = steps.filter((candidate) => candidate.phase === step.phase);
//   return { index: inPhase.indexOf(step) + 1, total: inPhase.length };
// };

/** True for the first after-arrival step, which is where the rail breaks. */
export const startsAfterArrival = (
  steps: OnboardingStep[],
  index: number
): boolean =>
  steps[index].phase === "after" &&
  (index === 0 || steps[index - 1].phase === "before");
